import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  useEventCategories,
  useCreateEventCategory,
  useUpdateEventCategory,
  type CreateEventCategoryData,
  type UpdateEventCategoryData,
} from "@/hooks/useEventManagement";

const eventCategorySchema = z.object({
  category_key: z
    .string()
    .min(2, "Category key must be at least 2 characters")
    .max(50, "Category key must be less than 50 characters")
    .regex(/^[a-z0-9_-]+$/, "Category key can only contain lowercase letters, numbers, hyphens, and underscores"),
  display_name: z
    .string()
    .min(2, "Display name must be at least 2 characters")
    .max(100, "Display name must be less than 100 characters"),
  description: z
    .string()
    .max(500, "Description must be less than 500 characters")
    .optional(),
  sort_order: z
    .number()
    .min(0, "Sort order must be a positive number")
    .max(1000, "Sort order must be less than 1000"),
  is_active: z.boolean().optional(),
});

type EventCategoryFormData = z.infer<typeof eventCategorySchema>;

interface EventCategoryFormProps {
  categoryId?: string | null;
  onClose: () => void;
}

export function EventCategoryForm({ categoryId, onClose }: EventCategoryFormProps) {
  const { data: categories } = useEventCategories();
  const createCategory = useCreateEventCategory();
  const updateCategory = useUpdateEventCategory();

  const isEditing = !!categoryId;
  const existingCategory = categories?.find(c => c.id === categoryId);

  const form = useForm<EventCategoryFormData>({
    resolver: zodResolver(eventCategorySchema),
    defaultValues: {
      category_key: "",
      display_name: "",
      description: "",
      sort_order: 0,
      is_active: true,
    },
  });

  useEffect(() => {
    if (existingCategory) {
      form.reset({
        category_key: existingCategory.category_key,
        display_name: existingCategory.display_name,
        description: existingCategory.description || "",
        sort_order: existingCategory.sort_order,
        is_active: existingCategory.is_active,
      });
    } else if (!isEditing) {
      // Set default sort_order for new categories
      const maxSortOrder = categories?.reduce((max, cat) => Math.max(max, cat.sort_order), 0) || 0;
      form.setValue("sort_order", maxSortOrder + 1);
    }
  }, [existingCategory, categories, form, isEditing]);

  const onSubmit = async (data: EventCategoryFormData) => {
    try {
      if (isEditing && categoryId) {
        const updates: UpdateEventCategoryData = {
          display_name: data.display_name,
          description: data.description,
          sort_order: data.sort_order,
          is_active: data.is_active,
        };
        await updateCategory.mutateAsync({ id: categoryId, updates });
      } else {
        const createData: CreateEventCategoryData = {
          category_key: data.category_key,
          display_name: data.display_name,
          description: data.description,
          sort_order: data.sort_order,
        };
        await createCategory.mutateAsync(createData);
      }
      onClose();
    } catch (error) {
      console.error("Failed to save event category:", error);
    }
  };

  const isLoading = createCategory.isPending || updateCategory.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="category_key"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Category Key</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., status_change, payment, delivery"
                  disabled={isEditing} // Category key can't be changed after creation
                />
              </FormControl>
              <FormDescription>
                {isEditing 
                  ? "Category key cannot be changed after creation"
                  : "Unique identifier for this category (lowercase, hyphens, underscores allowed)"
                }
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="display_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Display Name</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., Status Changes, Payment Events, Delivery Updates"
                />
              </FormControl>
              <FormDescription>
                Human-readable name shown in the interface
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  placeholder="Brief description of when this category is used..."
                  rows={3}
                />
              </FormControl>
              <FormDescription>
                Optional description explaining the purpose of this category
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="sort_order"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sort Order</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="number"
                  min="0"
                  max="1000"
                  onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                />
              </FormControl>
              <FormDescription>
                Used for ordering categories (lower numbers appear first)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {isEditing && (
          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Active</FormLabel>
                  <FormDescription>
                    Whether this category is available for use
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : isEditing ? "Update Category" : "Create Category"}
          </Button>
        </div>
      </form>
    </Form>
  );
}